import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormItem from 'src/components/form-item';
import './style.scss';
import { noop } from 'lodash';
import Button, { Type as ButtonType } from 'src/components/button';
import { CloseOutlined } from '@ant-design/icons';
import SimpleSelect from 'src/components/simple-select';

/**
 * A Need component for rendering filters.
 * @param {Object} props - Function component props.
 * @param {{}} props.filters - An object with all existing options for filters.
 * @param {Function} props.onApiFilter - Function to be called when changing filters.
 * @returns {JSX.Element} The Filters component.
 * @example
 *  <Filters onApiFilter={handleFilter} filters={filters} />
 */
function Filters({ filters, onApiFilter }) {
	const [site, setSite] = useState([]);
	const [team, setTeam] = useState([]);
	const [client, setClient] = useState([]);
	const [role, setRole] = useState([]);
	const [technology, setTechnology] = useState([]);
	const [level, setLevel] = useState([]);
	const [englishLevel, setEnglishLevel] = useState([]);
	const [requisitionManager, setRequisitionManager] = useState([]);
	const [requisitionTalentAttraction, setRequisitionTalentAttraction] =
		useState([]);
	const [pdm, setPdm] = useState([]);
	const [interviewRequired, setInterviewRequired] = useState([]);
	const [clearFilter, setClearFilter] = useState(false);

	const { t } = useTranslation();

	const buildCurrentFilters = () => ({
		site,
		team,
		client,
		role,
		technology,
		level,
		englishLevel,
		requisitionManager,
		requisitionTalentAttraction,
		pdm,
		interviewRequired,
	});

	const handleDropdownClose = (open) => {
		if (!open) onApiFilter(buildCurrentFilters());
	};

	const handleDeselect = (filterKey, currentValues) => (removedValue) => {
		onApiFilter({
			...buildCurrentFilters(),
			[filterKey]: currentValues.filter((v) => v !== removedValue),
		});
	};

	const handleSelect = (filterKey, currentValues) => (selectedValue) => {
		onApiFilter({
			...buildCurrentFilters(),
			[filterKey]: [...currentValues, selectedValue],
		});
	};

	const handleClear = (filterKey) => () => {
		onApiFilter({ ...buildCurrentFilters(), [filterKey]: [] });
	};

	/**
	 * Clear filters upon click.
	 */
	const handleClearFilter = () => {
		setClearFilter(true);
		setTimeout(() => {
			setClearFilter(false);
		}, 100);
	};

	return (
		<div className='opportunity-filter__container'>
			<FormItem label={t('site')} id='location'>
				<SimpleSelect
					options={filters.site}
					onChange={setSite}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('site', site)}
					onDeselect={handleDeselect('site', site)}
					onClear={handleClear('site')}
					aria-label={t('locationFilter')}
					aria-labelledby='location'
				/>
			</FormItem>
			<FormItem label={t('client')} id='client'>
				<SimpleSelect
					options={filters.client}
					onChange={setClient}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('client', client)}
					onDeselect={handleDeselect('client', client)}
					onClear={handleClear('client')}
					aria-label={t('clientFilter')}
					aria-labelledby='client'
				/>
			</FormItem>
			<FormItem label={`GU / ${t('alliance')}`} id='alliance'>
				<SimpleSelect
					options={filters.team}
					onChange={setTeam}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('team', team)}
					onDeselect={handleDeselect('team', team)}
					onClear={handleClear('team')}
					aria-label={t('guFilter')}
					aria-labelledby='alliance'
				/>
			</FormItem>
			<FormItem label={t('englishLevel')} id='english-level'>
				<SimpleSelect
					options={filters.englishLevel}
					onChange={setEnglishLevel}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('englishLevel', englishLevel)}
					onDeselect={handleDeselect('englishLevel', englishLevel)}
					onClear={handleClear('englishLevel')}
					aria-label={t('englishLevelFilter')}
					aria-labelledby='english-level'
				/>
			</FormItem>
			<FormItem label={t('role')} id='role'>
				<SimpleSelect
					options={filters.role}
					onChange={setRole}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('role', role)}
					onDeselect={handleDeselect('role', role)}
					onClear={handleClear('role')}
					aria-label={t('roleFilter')}
					aria-labelledby='role'
				/>
			</FormItem>
			<FormItem label={t('tech')} id='technology'>
				<SimpleSelect
					options={filters.technology}
					onChange={setTechnology}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('technology', technology)}
					onDeselect={handleDeselect('technology', technology)}
					onClear={handleClear('technology')}
					aria-label={t('techFilter')}
					aria-labelledby='technology'
				/>
			</FormItem>
			<FormItem label={t('positionMap')} id='position-map'>
				<SimpleSelect
					options={filters.level}
					onChange={setLevel}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('level', level)}
					onDeselect={handleDeselect('level', level)}
					onClear={handleClear('level')}
					aria-label={t('positionMapFilter')}
					aria-labelledby='position-map'
				/>
			</FormItem>
			<FormItem label={t('hiringManager')} id='hiring-manager'>
				<SimpleSelect
					options={filters.requisitionManager}
					onChange={setRequisitionManager}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('requisitionManager', requisitionManager)}
					onDeselect={handleDeselect('requisitionManager', requisitionManager)}
					onClear={handleClear('requisitionManager')}
					aria-label={t('hiringManagerFilter')}
					aria-labelledby='hiring-manager'
				/>
			</FormItem>
			<FormItem label={t('talentAttraction')} id='talent-attraction'>
				<SimpleSelect
					options={filters.requisitionTalentAttraction}
					onChange={setRequisitionTalentAttraction}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('requisitionTalentAttraction', requisitionTalentAttraction)}
					onDeselect={handleDeselect('requisitionTalentAttraction', requisitionTalentAttraction)}
					onClear={handleClear('requisitionTalentAttraction')}
					aria-label={t('talentAttractionFilter')}
					aria-labelledby='talent-attraction'
				/>
			</FormItem>
			<FormItem label={t('pdm')} id='pdm'>
				<SimpleSelect
					options={filters.pdm}
					onChange={setPdm}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('pdm', pdm)}
					onDeselect={handleDeselect('pdm', pdm)}
					onClear={handleClear('pdm')}
					aria-label={t('pdmFilter')}
					aria-labelledby='pdm'
				/>
			</FormItem>
			<FormItem label={t('interviewRequired')} id='interview-required'>
				<SimpleSelect
					options={filters.interviewRequired}
					onChange={setInterviewRequired}
					clear={clearFilter}
					onDropdownVisibleChange={handleDropdownClose}
					onSelect={handleSelect('interviewRequired', interviewRequired)}
					onDeselect={handleDeselect('interviewRequired', interviewRequired)}
					onClear={handleClear('interviewRequired')}
					aria-label={t('interviewRequiredFilter')}
					arial-labelledby='interview-required'
				/>
			</FormItem>
			<FormItem>
				<Button
					onClick={() => handleClearFilter()}
					type={ButtonType.Clear}
					uppercase
					aria-label={t('clearFilters')}
					id='clear-filters'
					className='btn-clear-filters'
				>
					<span className='btn-clear-content'>
						<CloseOutlined className='icon-x' />
						<span className='btn-clear-text'>{t('clearFilters')}</span>
					</span>
				</Button>
			</FormItem>
		</div>
	); 
}

Filters.defaultProps = {
	filters: [],
	onApiFilter: noop,
};

export default Filters;
